import { Target, TrendingUp, BarChart3, Zap, LineChart, Shield } from "lucide-react";
import SubServicePageLayout from "@/components/SubServicePageLayout";

const services = [
  {
    name: "Strategy & Planning",
    icon: Target,
    components: [
      "Audience research and segmentation",
      "Channel mix optimization",
      "Budget allocation modeling",
    ],
    outcome: "Media strategy aligned with business objectives.",
  },
  {
    name: "Campaign Execution",
    icon: Zap,
    components: [
      "Platform setup and configuration",
      "Audience targeting and exclusions",
      "Creative testing frameworks",
    ],
    outcome: "Technically sound campaigns that maximize performance.",
  },
  {
    name: "Real-Time Optimization",
    icon: TrendingUp,
    components: [
      "Bid and budget management",
      "Audience expansion and refinement",
      "Creative rotation and scaling",
    ],
    outcome: "Continuous improvement based on live data.",
  },
  {
    name: "Measurement & Attribution",
    icon: BarChart3,
    components: [
      "Conversion tracking setup",
      "Multi-touch attribution",
      "Incrementality testing",
    ],
    outcome: "Accurate understanding of media contribution.",
  },
  {
    name: "Reporting & Insights",
    icon: LineChart,
    components: [
      "Custom dashboard development",
      "Weekly and monthly reporting",
      "Strategic insights and recommendations",
    ],
    outcome: "Actionable insights that inform decisions.",
  },
  {
    name: "Brand Safety & Compliance",
    icon: Shield,
    components: [
      "Brand safety settings and verification",
      "Ad fraud prevention",
      "Privacy compliance management",
    ],
    outcome: "Media that protects your brand reputation.",
  },
];

const processSteps = [
  {
    number: "01",
    title: "Audit & Baseline",
    description: "Review current performance, tracking, and opportunities for improvement.",
  },
  {
    number: "02",
    title: "Strategy Development",
    description: "Build a media strategy with clear KPIs, targets, and channel priorities.",
  },
  {
    number: "03",
    title: "Campaign Build",
    description: "Set up campaigns with proper structure, targeting, and tracking.",
  },
  {
    number: "04",
    title: "Optimization Cycles",
    description: "Continuous testing, learning, and optimization based on performance data.",
  },
  {
    number: "05",
    title: "Scale & Expand",
    description: "Increase budget behind winning strategies and test new opportunities.",
  },
];

const technologies = [
  { category: "Search", items: ["Google Ads", "Microsoft Ads", "Apple Search Ads"] },
  { category: "Social", items: ["Meta Ads", "LinkedIn Ads", "TikTok Ads", "Pinterest Ads"] },
  { category: "Analytics", items: ["GA4", "Adobe Analytics", "Segment", "Mixpanel"] },
  { category: "Attribution", items: ["Triple Whale", "Northbeam", "Rockerbox", "AppsFlyer"] },
];

const faqs = [
  {
    question: "How do you approach budget allocation across channels?",
    answer: "We use data to guide allocation but don't rely solely on last-click attribution. We consider incrementality, customer journey complexity, and strategic objectives.",
  },
  {
    question: "What's your approach to creative testing?",
    answer: "Structured, iterative testing with clear hypotheses. We test one variable at a time where possible and prioritize statistical significance over gut feelings.",
  },
  {
    question: "How do you handle iOS 14+ tracking challenges?",
    answer: "We combine first-party data strategies, server-side tracking, and probabilistic modeling. We also work with attribution partners to provide the clearest possible picture.",
  },
  {
    question: "Do you manage creative production?",
    answer: "We provide creative strategy and performance insights, but typically work with your internal team or creative partners for production. We can recommend partners if needed.",
  },
  {
    question: "What's your minimum budget requirement?",
    answer: "We typically work with brands spending at least $50K monthly on paid media. Below that, the complexity of our approach may not deliver the best ROI.",
  },
];

const qualification = {
  forYou: [
    "You're spending $50K+ monthly on paid media",
    "You want a strategic partner, not just execution",
    "You're committed to testing and iteration",
    "You have clear business goals beyond vanity metrics",
  ],
  notForYou: [
    "You want to set and forget campaigns",
    "You're not willing to invest in proper tracking",
    "You expect overnight results without iteration",
    "You're looking for the cheapest media buying option",
  ],
};

const PerformanceMediaBuying = () => {
  return (
    <SubServicePageLayout
      seoTitle="Performance Media Buying Services"
      seoDescription="Data-driven media buying that maximizes ROI. Strategy, execution, and optimization across search, social, and programmatic channels."
      canonicalUrl="https://xperfomax.com/media-activations/performance-media-buying"
      category="media-activations"
      categoryLabel="Media Activations / Performance Media Buying"
      headline="Media buying that drives results."
      subheadline="We buy media with one goal: maximizing return on every dollar spent. Data-driven strategy, precise execution, continuous optimization."
      problemTitle="Why most media buying underperforms."
      problems={[
        "Set-it-and-forget-it campaign management.",
        "Attribution gaps that hide true performance.",
        "Over-reliance on platform algorithms without strategic oversight.",
        "Creative testing that's random rather than systematic.",
      ]}
      systemTitle="A performance system for media buying."
      systemDescription="Strategy, execution, and optimization working in continuous cycles."
      services={services}
      processTitle="From audit to scale."
      processSteps={processSteps}
      technologies={technologies}
      techTitle="Platforms"
      techDescription="Certified and experienced across all major advertising platforms."
      qualification={qualification}
      faqs={faqs}
      finalCtaTitle="Ready for media that performs?"
      finalCtaDescription="Let's discuss how data-driven media buying can improve your marketing ROI."
      finalCtaButtonText="Start a serious conversation"
    />
  );
};

export default PerformanceMediaBuying;
